import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Zap, BarChart3, Shield, ChevronRight, Menu, X, Globe, Clock, UserX, MessageCircleOff, Bot, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../App';
import Footer from '../components/Footer';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/product" className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors" data-testid="nav-product">Product</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors" data-testid="nav-pricing">Pricing</Link>
            <Link to="/about" className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors" data-testid="nav-about">About</Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors" data-testid="nav-contact">Contact</Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <select 
              value={language} 
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-sm"
              data-testid="language-selector"
            >
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="fr">FR</option>
              <option value="de">DE</option>
            </select>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-btn">Log in</Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="signup-btn">
                Get Started
              </Button>
            </Link>
          </div>

          <button 
            className="md:hidden p-2" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-col gap-4">
              <Link to="/product" className="text-slate-600 hover:text-slate-900 dark:text-slate-300">Product</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 dark:text-slate-300">Pricing</Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 dark:text-slate-300">About</Link>
              <Link to="/contact" className="text-slate-600 hover:text-slate-900 dark:text-slate-300">Contact</Link>
              <div className="flex gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full">Log in</Button>
                </Link>
                <Link to="/signup" className="flex-1">
                  <Button className="w-full bg-emerald-700 hover:bg-emerald-800">Get Started</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const LandingPage = () => {
  const features = [
    {
      icon: MessageSquare,
      title: "Unified Inbox",
      description: "Manage all customer conversations from WhatsApp in one centralized dashboard."
    },
    {
      icon: Zap,
      title: "AI-Powered Replies",
      description: "Let AI understand intent, suggest replies, and automate responses intelligently."
    },
    {
      icon: BarChart3,
      title: "Smart Analytics",
      description: "Track conversation metrics, response times, and customer satisfaction in real-time."
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade encryption with full audit trails and role-based access control."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <Zap className="w-4 h-4" />
              AI-Powered WhatsApp Business Platform
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 animate-slide-up" data-testid="hero-title">
              Your Business,{" "}
              <span className="text-emerald-700 dark:text-emerald-400">Connected</span>{" "}
              Through WhatsApp
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 mb-10 max-w-2xl mx-auto animate-slide-up stagger-1">
              {process.env.REACT_APP_APP_NAME || 'Flowteras'} is the intelligent business operating system that transforms how you manage customer conversations, automate workflows, and grow your business—all through WhatsApp.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up stagger-2">
              <Link to="/signup">
                <Button className="btn-primary text-lg px-10 py-6" data-testid="cta-get-started">
                  Get Started Free
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/product">
                <Button variant="outline" className="btn-secondary text-lg px-10 py-6" data-testid="cta-see-how">
                  See How It Works
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Hero Image/Demo */}
          <div className="mt-16 relative animate-slide-up stagger-3">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-lime-500/20 blur-3xl -z-10"></div>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="h-8 bg-slate-100 dark:bg-slate-800 flex items-center gap-2 px-4">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-12 gap-6">
                  {/* Sidebar */}
                  <div className="md:col-span-3 space-y-3">
                    <div className="bg-emerald-700 text-white rounded-lg p-3 flex items-center gap-3">
                      <MessageSquare className="w-5 h-5" />
                      <span className="font-medium">Inbox</span>
                    </div>
                    {['FAQ', 'Workflows', 'Analytics'].map((item, i) => (
                      <div key={i} className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 flex items-center gap-3 text-slate-600 dark:text-slate-300">
                        <div className="w-5 h-5 rounded bg-slate-200 dark:bg-slate-700"></div>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Conversation List */}
                  <div className="md:col-span-4 space-y-3">
                    {[
                      { name: 'Maria Santos', msg: 'When will my order arrive?', time: '2m', unread: true },
                      { name: 'John Doe', msg: 'Thanks for the quick response!', time: '15m', unread: false },
                      { name: 'Sarah Wilson', msg: 'I need help with...', time: '1h', unread: false }
                    ].map((conv, i) => (
                      <div key={i} className={`p-4 rounded-xl ${i === 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-slate-900 dark:text-white">{conv.name}</span>
                          <span className="text-xs text-slate-500">{conv.time}</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 truncate">{conv.msg}</p>
                        {conv.unread && <div className="mt-2 w-2 h-2 rounded-full bg-emerald-500"></div>}
                      </div>
                    ))}
                  </div>
                  
                  {/* Chat View */}
                  <div className="md:col-span-5 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                    <div className="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-700">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center">
                        <span className="font-medium text-emerald-700 dark:text-emerald-300">MS</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Maria Santos</p>
                        <p className="text-xs text-slate-500">+1 234 567 8900</p>
                      </div>
                    </div>
                    <div className="py-4 space-y-3">
                      <div className="message-customer max-w-[80%]">
                        <p className="text-sm">When will my order arrive?</p>
                      </div>
                      <div className="message-ai ml-auto max-w-[80%] relative">
                        <div className="absolute -top-2 -left-2 bg-lime-500 text-[10px] text-white px-2 py-0.5 rounded-full font-medium">AI Suggested</div>
                        <p className="text-sm">Your order #12345 is scheduled for delivery tomorrow between 2-4 PM. Would you like tracking updates?</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Businesses Lose Sales Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white mb-4">
              Why Businesses Lose Sales on WhatsApp
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Stop leaving money on the table. See how {process.env.REACT_APP_APP_NAME || 'Flowteras'} fixes common WhatsApp problems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Human Staff Problems */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-8 border border-red-100 dark:border-red-800/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                  <UserX className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-red-900 dark:text-red-200">Human Staff Only</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-300"><strong>Missed messages</strong> during peak hours and breaks</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-300"><strong>Slow replies</strong> causing customers to go elsewhere</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-300"><strong>Inconsistent tone</strong> damaging brand experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-300"><strong>No follow-up</strong> on interested leads</span>
                </li>
              </ul>
            </div>

            {/* Flowteras Solution */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-8 border border-emerald-100 dark:border-emerald-800/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-900 dark:text-emerald-200">With {process.env.REACT_APP_APP_NAME || 'Flowteras'}</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-700 dark:text-slate-300"><strong>Instant replies</strong> 24/7, even at 3 AM</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-700 dark:text-slate-300"><strong>AI-powered answers</strong> that understand context</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-700 dark:text-slate-300"><strong>After-hours automation</strong> never misses a lead</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-700 dark:text-slate-300"><strong>Smart routing</strong> to the right team member</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/signup">
              <Button className="btn-primary text-lg px-8 py-5" data-testid="why-section-cta">
                Turn Your WhatsApp Into a 24/7 AI Staff
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white mb-4">
              Everything You Need to Scale
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Built for teams who want to deliver exceptional customer experiences through WhatsApp.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="marketing-card group hover:-translate-y-1 transition-all duration-300" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10">
            Join thousands of businesses using {process.env.REACT_APP_APP_NAME || 'Flowteras'} to deliver exceptional customer experiences.
          </p>
          <Link to="/signup">
            <Button className="btn-primary text-lg px-10 py-6" data-testid="cta-bottom">
              Start Your Free Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LandingPage;
